import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Loader2, User, Link as LinkIcon } from 'lucide-react';
import { API } from '@/App';

/**
 * CustomerLinkPicker — inline button + dialog to link a group_member row
 * (co-traveller) to an existing customer record. Once linked, the share map
 * for that customer automatically picks up this tour's dots.
 *
 * Props:
 *   onPick({ id, name, dob?, phone?, email? })  called when admin picks one
 *   linkedId   currently linked customer id, if any (shows "Linked" state)
 *   excludeId  primary customer on the tour — hidden from results
 */
const CustomerLinkPicker = ({ onPick, linkedId, excludeId, trigger, size = 'sm' }) => {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState(null);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || customers !== null) return;
    (async () => {
      setLoading(true);
      try {
        const t = localStorage.getItem('session_token');
        const res = await fetch(`${API}/pollen/customers`, {
          credentials: 'include',
          headers: t ? { Authorization: `Bearer ${t}` } : {},
        });
        if (res.ok) setCustomers(await res.json()); else setCustomers([]);
      } catch { setCustomers([]); }
      finally { setLoading(false); }
    })();
  }, [open, customers]);

  const filtered = useMemo(() => {
    if (!customers) return [];
    const term = q.trim().toLowerCase();
    return customers
      .filter(c => c.id !== excludeId)
      .filter(c => !term
        || (c.name || '').toLowerCase().includes(term)
        || (c.phone || '').toLowerCase().includes(term)
        || (c.email || '').toLowerCase().includes(term))
      .slice(0, 50);
  }, [customers, q, excludeId]);

  const pick = (c) => {
    onPick && onPick(c);
    setOpen(false);
    setQ('');
  };

  return (
    <>
      {trigger
        ? <span onClick={() => setOpen(true)}>{trigger}</span>
        : (
          <Button type="button" variant={linkedId ? 'secondary' : 'outline'} size={size} className="h-7 text-xs" onClick={() => setOpen(true)} data-testid="link-customer-btn">
            <LinkIcon className="w-3 h-3 mr-1" />{linkedId ? 'Change link' : 'Link customer'}
          </Button>
        )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base">Link to existing customer</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                autoFocus
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search by name, phone, or email…"
                className="pl-9"
                data-testid="link-customer-search"
              />
            </div>
            <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
              {loading || customers === null ? (
                <div className="py-8 flex items-center justify-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>
              ) : filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No customers match.</p>
              ) : filtered.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pick(c)}
                  className="w-full text-left px-3 py-2 hover:bg-muted/50 flex items-center gap-2 transition"
                  data-testid={`link-customer-option-${c.id}`}
                >
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <User className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{[c.phone, c.email].filter(Boolean).join(' · ') || '—'}</div>
                  </div>
                  {linkedId === c.id && <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">linked</span>}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">Linking shares this tour's map dots (hotels, places, drivers, guides) with the linked customer's journey.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerLinkPicker;
